import React, { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";

const S = {
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"1px 6px",borderRadius:10,background:bg,color:c,fontWeight:500}),
};

function fileIcon(name){
  const ext=(name||"").split(".").pop().toLowerCase();
  if(ext==="pdf") return "📄";
  if(["xlsx","xls","csv"].includes(ext)) return "📊";
  if(["doc","docx"].includes(ext)) return "📝";
  if(["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼";
  return "📎";
}

function formatDate(ts){
  if(!ts) return "";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
}

export default function GlobalSearch({ onNavigate, isAdmin, clientName }){
  const [query2, setQuery2] = useState("");
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const inputRef = useRef();
  const dropdownRef = useRef();

  useEffect(()=>{
    const unsubs = [];
    // Files
    unsubs.push(onSnapshot(collection(db,"files"), snap=>
      setFiles(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"file"})))
    ));
    // Tasks
    unsubs.push(onSnapshot(collection(db,"tasks"), snap=>
      setTasks(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"task"})))
    ));
    if(isAdmin){
      // Clients
      unsubs.push(onSnapshot(collection(db,"clients"), snap=>
        setClients(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"client"})))
      ));
      // Invoices
      unsubs.push(onSnapshot(collection(db,"invoices"), snap=>
        setInvoices(snap.docs.map(d=>({...d.data(),firestoreId:d.id,_type:"invoice"})))
      ));
    }
    return ()=>unsubs.forEach(u=>u());
  },[isAdmin]);

  useEffect(()=>{
    const handler = (e) => {
      if(dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);

  const q = query2.toLowerCase().trim();

  const results = useMemo(()=>{
    if(!q || q.length < 2) return [];
    const out = [];

    // Filter by client for non-admin
    const clientFilter = !isAdmin ? clientName : null;

    // Files
    const matchedFiles = files.filter(f=>{
      if(clientFilter && f.client !== clientFilter) return false;
      return (f.name||"").toLowerCase().includes(q) ||
             (f.client||"").toLowerCase().includes(q) ||
             (f.folderName||"").toLowerCase().includes(q) ||
             (f.year||"").includes(q);
    }).slice(0,5);
    matchedFiles.forEach(f=>out.push({
      id:f.firestoreId, type:"file", icon:fileIcon(f.name),
      title:f.name, subtitle:`${f.client} · ${f.folderName||"—"} · ${f.year||""}`,
      meta:formatDate(f.uploadedAt), url:f.url,
      action:()=>{ if(onNavigate) onNavigate("files", f.client); setOpen(false); setQuery2(""); }
    }));

    // Tasks
    const matchedTasks = tasks.filter(t=>{
      if(clientFilter && t.client !== clientFilter) return false;
      return (t.title||"").toLowerCase().includes(q) ||
             (t.client||"").toLowerCase().includes(q) ||
             (t.notes||"").toLowerCase().includes(q) ||
             (t.category||"").toLowerCase().includes(q);
    }).slice(0,3);
    matchedTasks.forEach(t=>out.push({
      id:t.firestoreId, type:"task", icon:"✅",
      title:t.title, subtitle:`${t.client||"No client"} · ${t.category||""}`,
      meta:t.status, metaBg:t.status==="Completed"?"#EAF3DE":"#E6F1FB", metaColor:t.status==="Completed"?"#3B6D11":"#185FA5",
      action:()=>{ if(onNavigate) onNavigate("tasks"); setOpen(false); setQuery2(""); }
    }));

    if(isAdmin){
      // Clients
      const matchedClients = clients.filter(c=>
        (c.name||"").toLowerCase().includes(q) ||
        (c.email||"").toLowerCase().includes(q) ||
        (c.contact||"").toLowerCase().includes(q)
      ).slice(0,3);
      matchedClients.forEach(c=>out.push({
        id:c.firestoreId, type:"client", icon:"👤",
        title:c.name, subtitle:`${c.contact||""} · ${c.email||""} · ${c.cityState||""}`,
        meta:c.status, metaBg:c.status==="Active"?"#EAF3DE":"#f5f5f0", metaColor:c.status==="Active"?"#3B6D11":"#888",
        action:()=>{ if(onNavigate) onNavigate("clients"); setOpen(false); setQuery2(""); }
      }));

      // Invoices
      const matchedInvoices = invoices.filter(i=>
        (i.client||"").toLowerCase().includes(q) ||
        (i.service||"").toLowerCase().includes(q) ||
        (i.amount||"").toLowerCase().includes(q) ||
        (i.num||"").toLowerCase().includes(q)
      ).slice(0,2);
      matchedInvoices.forEach(i=>out.push({
        id:i.firestoreId, type:"invoice", icon:"💳",
        title:`${i.num||""} — ${i.client}`, subtitle:`${i.service||""} · ${i.amount||""}`,
        meta:i.status, metaBg:i.status==="Paid"?"#EAF3DE":i.status==="Overdue"?"#FCEBEB":"#E6F1FB",
        metaColor:i.status==="Paid"?"#3B6D11":i.status==="Overdue"?"#A32D2D":"#185FA5",
        action:()=>{ if(onNavigate) onNavigate("invoicing"); setOpen(false); setQuery2(""); }
      }));
    }

    return out;
  },[q, files, tasks, clients, invoices, isAdmin, clientName]);

  const handleKeyDown = (e) => {
    if(e.key==="Escape"){ setOpen(false); setQuery2(""); }
    if(e.key==="Enter" && results.length>0){ results[0].action(); }
  };

  return (
    <div ref={dropdownRef} style={{position:"relative",flex:1,maxWidth:480}}>
      <div style={{position:"relative"}}>
        <input
          ref={inputRef}
          value={query2}
          onChange={e=>{ setQuery2(e.target.value); setOpen(true); }}
          onFocus={()=>setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search files, clients, tasks, invoices..."
          style={{
            width:"100%", padding:"7px 12px 7px 32px", fontSize:12,
            border:"0.5px solid #e0dfd8", borderRadius:20, fontFamily:"sans-serif",
            background:"#f5f5f0", boxSizing:"border-box", outline:"none",
            transition:"all 0.2s",
          }}
          onMouseEnter={e=>e.target.style.background="#fff"}
          onMouseLeave={e=>{ if(document.activeElement!==e.target) e.target.style.background="#f5f5f0"; }}
        />
        <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"#aaa",pointerEvents:"none"}}>🔍</span>
        {query2&&<span onClick={()=>{setQuery2("");setOpen(false);}} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:14,color:"#aaa"}}>✕</span>}
      </div>

      {open && query2.length >= 2 && (
        <div style={{
          position:"absolute",top:"calc(100% + 6px)",left:0,right:0,
          background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,
          boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:1000,overflow:"hidden",
          maxHeight:440,overflowY:"auto",
        }}>
          {results.length===0 ? (
            <div style={{padding:"20px 16px",textAlign:"center"}}>
              <div style={{fontSize:13,color:"#888",marginBottom:8}}>No results for "{query2}"</div>
              <button onClick={()=>{ if(onNavigate) onNavigate("advanced-search",query2); setOpen(false); }}
                style={{fontSize:12,padding:"6px 14px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"}}>
                🔎 Advanced Search
              </button>
            </div>
          ) : (
            <>
              {/* Group by type */}
              {["file","client","task","invoice"].map(type=>{
                const group = results.filter(r=>r.type===type);
                if(group.length===0) return null;
                const labels = {file:"Files",client:"Clients",task:"Tasks",invoice:"Invoices"};
                return (
                  <div key={type}>
                    <div style={{padding:"6px 14px 3px",fontSize:10,color:"#aaa",fontWeight:500,textTransform:"uppercase",letterSpacing:"0.06em",background:"#fafaf8",borderBottom:"0.5px solid #f0efe8"}}>
                      {labels[type]}
                    </div>
                    {group.map(r=>(
                      <div key={r.id} onClick={r.action} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",cursor:"pointer",borderBottom:"0.5px solid #f5f5f0"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{fontSize:18,flexShrink:0}}>{r.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.title}</div>
                          <div style={{fontSize:11,color:"#888",marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.subtitle}</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,flexShrink:0}}>
                          {r.meta&&<span style={r.metaBg ? S.badge(r.metaBg,r.metaColor) : {fontSize:10,color:"#aaa"}}>{r.meta}</span>}
                          {r.type==="file"&&<a href={r.url} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:10,color:"#185FA5",textDecoration:"none"}}>↓ open</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              <div style={{padding:"10px 14px",borderTop:"0.5px solid #f0efe8",background:"#fafaf8",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"#aaa"}}>{results.length} result{results.length!==1?"s":""} · Enter to open first</span>
                <button onClick={()=>{ if(onNavigate) onNavigate("advanced-search",query2); setOpen(false); }}
                  style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",color:"#555"}}>
                  🔎 Advanced Search
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
